import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  OrderScooterIcon,
  Spinner,
  WarningOctagonIcon,
  useModalControls,
} from '@noor/ui';
import { cn, type ModalRenderProps } from '@noor/lib';

/**
 * Confirmation modal registered under the `confirm` id; resolves the
 * `confirmDialog()` promise via `onResolve`. Every settle path — the confirm
 * button, the cancel button, and dismissal (backdrop / Esc, via
 * `onOpenChange`) — goes through `resolve`, which settles at most once. This
 * deliberately avoids an unmount-effect guard, which would resolve `false`
 * during React StrictMode's dev mount/unmount probe and pre-empt the user's
 * real choice.
 *
 * Two looks: the default `warning` prompt used app-wide, and the `courier`
 * variant from the Eats orders design (Figma 840:39154) — an orange scooter
 * over a 32px title, with the buttons centred rather than stretched.
 */
export function ConfirmModal({ payload }: ModalRenderProps<'confirm'>) {
  const {
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    variant = 'warning',
    onConfirm,
    onResolve,
  } = payload;
  const { open, close } = useModalControls();
  const courier = variant === 'courier';
  const [pending, setPending] = useState(false);

  // Settle the promise exactly once, from whichever path fires first — a button
  // press or a dismissal (backdrop / Esc). A ref (not an unmount effect) keeps
  // this StrictMode-safe: an unmount-cleanup guard would resolve `false` during
  // StrictMode's dev mount/unmount probe, pre-empting the user's real choice.
  const settledRef = useRef(false);
  const resolve = (confirmed: boolean): void => {
    if (!settledRef.current) {
      settledRef.current = true;
      onResolve(confirmed);
    }
    close();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!onConfirm) {
      resolve(true);
      return;
    }

    setPending(true);
    try {
      await onConfirm();
      resolve(true);
    } catch {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next || pending ? undefined : resolve(false))}
    >
      <DialogContent
        showClose={false}
        className={cn(
          'flex flex-col items-center gap-8 rounded-3xl p-6',
          courier ? 'max-w-[42.25rem] gap-4 py-14 sm:max-w-[42.25rem]' : 'max-w-[30rem]',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center',
            courier ? 'gap-4' : 'gap-6 px-4 sm:px-10',
          )}
        >
          {courier ? (
            <OrderScooterIcon className="h-[3.6rem] w-[4.5rem] shrink-0 text-primary" />
          ) : (
            <WarningOctagonIcon className="size-16 shrink-0" />
          )}
          <div className="flex flex-col items-center gap-2 text-center">
            <DialogTitle
              className={cn(
                'font-semibold tracking-tight text-foreground',
                courier
                  ? 'max-w-[29.25rem] text-[2rem] leading-8 font-bold tracking-[-0.04em]'
                  : 'text-[1.375rem]',
              )}
            >
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="text-sm font-medium tracking-tight text-muted-foreground">
                {description}
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{title}</DialogDescription>
            )}
          </div>
        </div>

        <div
          className={cn('flex w-full gap-2', courier && 'w-auto justify-center gap-4')}
        >
          <button
            type="button"
            onClick={() => resolve(false)}
            disabled={pending}
            className={cn(
              'flex cursor-pointer items-center justify-center rounded-full bg-muted p-4 text-base font-semibold tracking-tight text-foreground transition-colors hover:bg-muted/70 focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
              courier ? 'h-[3.1875rem] min-w-[7.4375rem]' : 'flex-1',
              pending && 'pointer-events-none opacity-50',
            )}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={pending}
            aria-busy={pending}
            className={cn(
              'flex cursor-pointer items-center justify-center rounded-full p-4 text-base font-semibold tracking-tight transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
              courier ? 'h-[3.1875rem]' : 'flex-1',
              destructive
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
          >
            {pending && <Spinner size="sm" className="text-current" />}
            {confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
